import { Server } from "@modelcontextprotocol/sdk/server/index.js";
import { StdioServerTransport } from "@modelcontextprotocol/sdk/server/stdio.js";
import type { Transport } from "@modelcontextprotocol/sdk/shared/transport.js";
import {
  CallToolRequestSchema,
  ListResourcesRequestSchema,
  ListToolsRequestSchema,
  ReadResourceRequestSchema,
} from "@modelcontextprotocol/sdk/types.js";
import { readFileSync, existsSync, statSync, readdirSync } from "node:fs";
import { join, relative } from "node:path";
import { createHash } from "node:crypto";
import { getFileGitInfo } from "../git.js";
import { ScaffoldSearchIndex, isSqliteAvailable } from "./search-index.js";
import type { CaiConfig } from "../types.js";
import { findScaffoldFiles, runDriftCheck } from "../drift/index.js";
import { estimateTokens, stripFrontmatter } from "../utils/fs.js";
import { scanProjectModel } from "../scanner/manifest.js";
import { KNOWN_EXTENSIONS } from "../utils/extensions.js";
import { appendQuery, aggregateByFile, readQueries } from "../telemetry/query-log.js";
import { buildSectionIndex, buildEdgeMap, extractSummary, type Section } from "./section-index.js";
import { globSync } from "glob";
import { routeContext } from "./context-router.js";
import { buildAdrIndex, findAdrFiles, resolveAdrStatus, type AdrEntry } from "../drift/checkers/adr.js";

interface CachedFile {
  raw: string;
  stripped: string;
  tokens: number;
  mtimeMs: number;
  hash: string;
}

/** Caches scaffold file list and contents to avoid repeated disk I/O */
class ScaffoldCache {
  private fileList: string[] = [];
  private files = new Map<string, CachedFile>();
  private lastGlobMs = 0;
  private readonly GLOB_TTL_MS = 30_000;
  private sectionIndex: Section[] | null = null;
  private edgeMap: Map<string, string[]> | null = null;
  private adrIndex: Map<string, AdrEntry> | null = null;
  private knownFilesSet: Set<string> | null = null;
  // Per-session read tracking for diff-based re-reads (P2.2)
  private sessionReads = new Map<string, string>(); // absPath → hash at last read
  private searchIndex: ScaffoldSearchIndex | null = null;
  private sqliteAvailable: boolean;

  constructor(
    private projectRoot: string,
    private scaffoldRoot: string
  ) {
    this.sqliteAvailable = isSqliteAvailable();
  }

  /** Get cached file list, re-globbing at most every 30s */
  getFileList(): string[] {
    const now = Date.now();
    if (now - this.lastGlobMs > this.GLOB_TTL_MS || this.fileList.length === 0) {
      this.fileList = findScaffoldFiles(this.projectRoot, this.scaffoldRoot);
      this.lastGlobMs = now;
      // Invalidate derived state when file list changes
      this.sectionIndex = null;
      this.edgeMap = null;
    }
    return this.fileList;
  }

  /** Get file content (stripped of frontmatter), using mtime-based cache */
  getFile(absPath: string): CachedFile {
    let mtimeMs: number;
    try {
      mtimeMs = statSync(absPath).mtimeMs;
    } catch {
      return { raw: "", stripped: "", tokens: 0, mtimeMs: 0, hash: "" };
    }

    const cached = this.files.get(absPath);
    if (cached && cached.mtimeMs === mtimeMs) return cached;

    let raw: string;
    try {
      raw = readFileSync(absPath, "utf8");
    } catch {
      raw = "";
    }
    const stripped = stripFrontmatter(raw);
    const hash = createHash("md5").update(stripped).digest("hex").slice(0, 8);
    const entry: CachedFile = { raw, stripped, tokens: estimateTokens(stripped), mtimeMs, hash };
    this.files.set(absPath, entry);
    return entry;
  }

  /**
   * Check if Claude has already read this file in the current session.
   * Returns "unseen" | "unchanged" | "changed".
   */
  getReadState(absPath: string): "unseen" | "unchanged" | "changed" {
    const prev = this.sessionReads.get(absPath);
    if (!prev) return "unseen";
    const current = this.getFile(absPath).hash;
    return prev === current ? "unchanged" : "changed";
  }

  /** Record that Claude read this file at its current hash. */
  markRead(absPath: string): void {
    const { hash } = this.getFile(absPath);
    this.sessionReads.set(absPath, hash);
  }

  /** Get or build the FTS5 search index. Rebuilds when files are stale. */
  getSearchIndex(): ScaffoldSearchIndex | null {
    if (!this.sqliteAvailable) return null;

    const dbPath = join(this.projectRoot, ".cai", ".cache", "search.sqlite");
    if (!this.searchIndex) {
      this.searchIndex = new ScaffoldSearchIndex(dbPath);
    }

    const files = this.getFileList();
    if (this.searchIndex.isStale(files)) {
      const indexed = files.map((absPath) => ({
        absPath,
        relPath: relative(this.projectRoot, absPath),
        content: this.getFile(absPath).stripped,
      }));
      this.searchIndex.rebuild(indexed);
    }

    return this.searchIndex;
  }

  /** Get section index, rebuilding when file list changes */
  getSectionIndex(): Section[] {
    if (!this.sectionIndex) {
      const files = this.getFileList();
      this.sectionIndex = buildSectionIndex(files, this.projectRoot, (absPath) => {
        return this.getFile(absPath).stripped;
      });
    }
    return this.sectionIndex;
  }

  /** Get edge map from frontmatter, rebuilding when file list changes */
  getEdgeMap(): Map<string, string[]> {
    if (!this.edgeMap) {
      const files = this.getFileList();
      this.edgeMap = buildEdgeMap(files, this.projectRoot, this.scaffoldRoot);
    }
    return this.edgeMap;
  }

  /** Get ADR index, built lazily from scaffold + dedicated ADR files */
  getAdrIndex(): Map<string, AdrEntry> {
    if (this.adrIndex) return this.adrIndex;
    const adrFiles = findAdrFiles(this.projectRoot, this.scaffoldRoot);
    const scaffoldFiles = this.getFileList();
    this.adrIndex = buildAdrIndex([...adrFiles, ...scaffoldFiles], this.projectRoot);
    return this.adrIndex;
  }

  /** Get Set of all relative file paths for O(1) existence checks. Built once per file-list cycle. */
  getKnownFilesSet(): Set<string> {
    if (this.knownFilesSet) return this.knownFilesSet;
    const files = globSync("**/*", {
      cwd: this.projectRoot,
      ignore: ["node_modules/**", ".cai/**", ".context-condensing/**", "dist/**", ".git/**", "build/**"],
      maxDepth: 6,
      nodir: true,
    });
    this.knownFilesSet = new Set(files);
    return this.knownFilesSet;
  }

  /** Force re-glob and clear file cache (for drift checks that need fresh data) */
  invalidate(): void {
    this.fileList = [];
    this.files.clear();
    this.sectionIndex = null;
    this.edgeMap = null;
    this.adrIndex = null;
    this.knownFilesSet = null;
    this.lastGlobMs = 0;
  }

  /** Release native handles (FTS5 sqlite) so the scaffold cache dir can be deleted. */
  dispose(): void {
    if (this.searchIndex) {
      this.searchIndex.close();
      this.searchIndex = null;
    }
  }
}

/** Extract only heading lines from markdown content */
function extractHeadings(content: string): string {
  return content
    .split("\n")
    .filter((line) => /^#{1,6}\s/.test(line))
    .join("\n");
}

/** Extract a specific section by heading name (case-insensitive partial match) */
function extractSection(content: string, sectionName: string): string {
  const lines = content.split("\n");
  const lower = sectionName.toLowerCase();
  let capturing = false;
  let captureLevel = 0;
  const out: string[] = [];

  for (const line of lines) {
    const headingMatch = line.match(/^(#{1,6})\s+(.+)/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const title = headingMatch[2].trim();
      if (capturing) {
        // Stop at same or higher level heading
        if (level <= captureLevel) break;
      }
      if (!capturing && title.toLowerCase().includes(lower)) {
        capturing = true;
        captureLevel = level;
      }
    }
    if (capturing) out.push(line);
  }

  return out.join("\n").trim();
}

/** Simple full-text search across scaffold files */
function searchScaffold(
  scaffoldFiles: string[],
  projectRoot: string,
  query: string,
  cache: ScaffoldCache,
  limit = 5
): Array<{ file: string; excerpt: string; line: number }> {
  const results: Array<{ file: string; excerpt: string; line: number }> = [];
  const q = query.toLowerCase();

  const maxPerFile = Math.max(1, Math.ceil(limit / Math.max(1, scaffoldFiles.length)));

  for (const absPath of scaffoldFiles) {
    const { raw: content } = cache.getFile(absPath);
    const lines = content.split("\n");
    let fileHits = 0;
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].toLowerCase().includes(q)) {
        const start = Math.max(0, i - 1);
        const end = Math.min(lines.length - 1, i + 2);
        results.push({
          file: relative(projectRoot, absPath),
          excerpt: lines.slice(start, end + 1).join("\n"),
          line: i + 1,
        });
        fileHits++;
        if (results.length >= limit) break;
        if (fileHits >= maxPerFile) break;
      }
    }
    if (results.length >= limit) break;
  }

  return results;
}

interface DynamicTool {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
}

type ProjectModel = ReturnType<typeof scanProjectModel>;

/** Generate additional MCP tools based on detected project type */
function getDynamicTools(project: ProjectModel): DynamicTool[] {
  const tools: DynamicTool[] = [];

  if (project.rootManifest?.type === "package.json" && project.commands.length > 0) {
    tools.push({
      name: "cai_project_commands",
      description:
        `List all ${project.commands.length} available project commands (npm scripts, make targets). ` +
        "Use this to find the right command before running anything.",
      inputSchema: { type: "object", properties: {} },
    });
  }

  if (project.workspaces.length > 1) {
    tools.push({
      name: "cai_workspace_map",
      description:
        `Navigate this monorepo's ${project.workspaces.length} workspaces. ` +
        "Returns workspace names, paths, and internal dependency graph.",
      inputSchema: { type: "object", properties: {} },
    });
  }

  return tools;
}

/** Handle dynamic tool calls */
function handleDynamicTool(name: string, project: ProjectModel): string | null {

  if (name === "cai_project_commands") {
    const commands = project.commands;
    if (commands.length === 0) return "No commands found in project manifests.";
    const rows = commands.map((c) => `- \`${c}\``).join("\n");
    return `## Project Commands (${commands.length})\n\n${rows}`;
  }

  if (name === "cai_workspace_map") {
    if (project.workspaces.length === 0) return "No workspaces detected.";
    const lines: string[] = [`## Workspaces (${project.workspaces.length})\n`];
    for (const ws of project.workspaces) {
      const wsName = ws.manifest.name ?? ws.path;
      const deps = project.workspaceDependencies
        .filter((d) => d.from === ws.path)
        .map((d) => d.to);
      const depStr = deps.length > 0 ? ` → depends on: ${deps.join(", ")}` : "";
      lines.push(`- **${wsName}** (\`${ws.path}\`)${depStr}`);
    }
    return lines.join("\n");
  }

  return null;
}

/**
 * Orphan detection (P2.3): find .cai/patterns/*.md files that have never been
 * queried via MCP (or not in the last 30 days). These are "ghost tokens" —
 * they sit in the scaffold and consume context budget but Claude never reads them.
 */
function detectOrphanedPatterns(projectRoot: string, scaffoldRoot: string): string {
  const patDir = join(scaffoldRoot, "patterns");
  if (!existsSync(patDir)) return "";

  let patternFiles: string[];
  try {
    patternFiles = readdirSync(patDir).filter((f) => f.endsWith(".md"));
  } catch {
    return "";
  }

  if (patternFiles.length === 0) return "";

  const queries = readQueries(projectRoot);
  const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
  const recentlyRead = new Set(
    queries
      .filter((q) => q.ts > thirtyDaysAgo && q.file)
      .map((q) => q.file as string)
  );

  const orphans = patternFiles.filter((f) => !recentlyRead.has(`patterns/${f}`));
  if (orphans.length === 0) return "";

  return [
    `⚠ ${orphans.length} orphaned pattern${orphans.length !== 1 ? "s" : ""} (not read via MCP in 30 days — possible ghost tokens):`,
    ...orphans.map((f) => `  · patterns/${f}`),
  ].join("\n");
}

const PATH_RE = /`([^`]{3,80})`/g;

/**
 * Extract verified paths from context sections.
 * Uses a pre-built file set for O(1) existence checks instead of per-path existsSync.
 * Returns a deduplicated, sorted list of confirmed paths. Max 20 entries.
 */
function buildVerifiedPaths(
  sections: Array<{ id: string; content: string }>,
  knownFiles: Set<string>
): string[] {
  const seen = new Set<string>();
  const paths: string[] = [];

  for (const section of sections) {
    if (paths.length >= 20) break;
    for (const [, raw] of section.content.matchAll(PATH_RE)) {
      if (paths.length >= 20) break;
      if (!KNOWN_EXTENSIONS.test(raw) && !raw.includes("/")) continue;
      if (raw.includes("://") || raw.includes("&&") || raw.includes("<")) continue;
      const normalized = raw.replace(/^\.?\//, "");
      if (!normalized || seen.has(normalized)) continue;
      seen.add(normalized);
      if (!knownFiles.has(normalized)) continue;
      paths.push(normalized);
    }
  }

  return paths.sort();
}

/**
 * Map a model name or tier to a token budget.
 * "expensive" models (Opus) get a tighter default; "cheap" models (Haiku, Sonnet) get more.
 * Explicit `budget` param always overrides this.
 */
function resolveModelBudget(model: string | undefined): number {
  if (!model) return 4000;
  const lower = model.toLowerCase();
  // Opus / expensive tier — keep context tight
  if (lower.includes("opus") || lower === "expensive" || lower === "large") return 2000;
  // Haiku / fast / cheap tier — can afford more context
  if (lower.includes("haiku") || lower === "cheap" || lower === "small" || lower === "fast") return 8000;
  // Sonnet / medium — default
  return 4000;
}

/**
 * Build a one-line freshness note for a scaffold file.
 * Returns empty string when file is fresh or git info is unavailable.
 * Example: " ⚠ possibly stale — last updated 12d ago, 45 commits since then"
 */
async function buildFreshnessNote(relPath: string, projectRoot: string): Promise<string> {
  try {
    const info = await getFileGitInfo(relPath, projectRoot);
    if (info.days === null && info.commits === null) return "";

    const parts: string[] = [];
    if (info.days !== null) parts.push(`last updated ${info.days}d ago`);
    if (info.commits !== null && info.commits > 0) parts.push(`${info.commits} commits since then`);

    if (parts.length === 0) return "";

    const isStale = (info.days !== null && info.days >= 14) || (info.commits !== null && info.commits >= 20);
    if (!isStale) return "";

    return ` ⚠ possibly stale — ${parts.join(", ")}`;
  } catch {
    return "";
  }
}

/** Derive a section's source relpath from its id ("context/architecture.md#Heading" → "context/architecture.md"). */
export function sectionRelPath(sectionId: string): string {
  return sectionId.split("#")[0];
}

/**
 * Decide, per section, whether its content should be stubbed (file already read
 * unchanged this session) or emitted. The gate is FILE-level because the underlying
 * read-state tracking is file-granular: if a file is unchanged, all its sections are
 * stubbed together; mixing would risk stubbing a section the agent never actually saw.
 * Pure and injectable for testing.
 */
export function partitionSectionsForDedup(
  sectionIds: string[],
  isFileUnchanged: (relPath: string) => boolean
): Array<{ id: string; relPath: string; stub: boolean }> {
  const decided = new Map<string, boolean>();
  return sectionIds.map((id) => {
    const relPath = sectionRelPath(id);
    let stub = decided.get(relPath);
    if (stub === undefined) {
      stub = isFileUnchanged(relPath);
      decided.set(relPath, stub);
    }
    return { id, relPath, stub };
  });
}

/**
 * Compute a freshness note per section id, sharing one git lookup per source file.
 * Sections frequently share files; without this cache each section would trigger a
 * sequential git spawn and break the router's millisecond-runtime promise.
 * `noteFn` is injectable so callers (and tests) can supply buildFreshnessNote or a stub.
 */
export async function buildSectionFreshnessNotes(
  sectionIds: string[],
  noteFn: (relPath: string) => Promise<string>
): Promise<Map<string, string>> {
  const byFile = new Map<string, string>();
  const byId = new Map<string, string>();
  for (const id of sectionIds) {
    const relPath = sectionRelPath(id);
    let note = byFile.get(relPath);
    if (note === undefined) {
      note = await noteFn(relPath);
      byFile.set(relPath, note);
    }
    byId.set(id, note);
  }
  return byId;
}

export interface McpServerHandle {
  server: Server;
  /** Close the MCP server AND release any native handles (FTS5 sqlite, etc.). */
  close: () => Promise<void>;
}

export async function startMcpServer(
  config: CaiConfig,
  transport?: Transport
): Promise<McpServerHandle> {
  const { projectRoot, scaffoldRoot } = config;
  const profile = (process.env.CAI_PROFILE ?? "full") as "lean" | "full";

  const cache = new ScaffoldCache(projectRoot, scaffoldRoot);
  const projectModel = scanProjectModel(projectRoot);

  const server = new Server(
    { name: "cai", version: "1.0.0" },
    { capabilities: { resources: {}, tools: {} } }
  );

  // ── Resources: each .cai/ scaffold file ──
  server.setRequestHandler(ListResourcesRequestSchema, async () => {
    const files = cache.getFileList();
    return {
      resources: files.map((absPath) => {
        const rel = relative(projectRoot, absPath);
        const { tokens } = cache.getFile(absPath);
        return {
          uri: `cai://scaffold/${rel}`,
          name: rel,
          description: `Scaffold file (~${tokens} tokens)`,
          mimeType: "text/markdown",
        };
      }),
    };
  });

  server.setRequestHandler(ReadResourceRequestSchema, async (req) => {
    const uri = req.params.uri;
    if (!uri.startsWith("cai://scaffold/")) {
      throw new Error(`Unknown resource URI: ${uri}`);
    }
    const rel = uri.slice("cai://scaffold/".length);
    const absPath = join(projectRoot, rel);
    if (!existsSync(absPath)) {
      throw new Error(`Scaffold file not found: ${rel}`);
    }
    const { stripped } = cache.getFile(absPath);
    return {
      contents: [{ uri, mimeType: "text/markdown", text: stripped }],
    };
  });

  // ── Tools ──
  // lean profile: only the 3 most-used tools (~150 token handshake vs ~400 for full)
  const coreTools = [
    {
      name: "cai_list_context",
      description:
        "START HERE. List all project context files with token costs. " +
        "Call this first to see what documentation exists before reading files or answering architecture questions.",
      inputSchema: { type: "object", properties: {} },
    },
    {
      name: "cai_get_context",
      description:
        "Read a project context file. Default mode is 'summary' (~10% tokens). " +
        "Use mode='headings' (~3%) for structure only, mode='full' only when you need exact details. " +
        "Covers: architecture, conventions, tech stack, setup, decisions.",
      inputSchema: {
        type: "object",
        properties: {
          path: {
            type: "string",
            description: "Relative path from cai_list_context output (e.g. '.cai/context/architecture.md')",
          },
          mode: {
            type: "string",
            enum: ["full", "summary", "headings"],
            description:
              "'headings': structure only (~3% tokens). 'summary': headings + first line (~10%). 'full': entire file (100%).",
          },
          section: {
            type: "string",
            description: "Optional heading to extract (e.g. 'Key Components'). Returns only that section. Saves tokens when you need one specific part.",
          },
        },
        required: ["path"],
      },
    },
    {
      name: "cai_search",
      description:
        "Search all project documentation for a keyword or topic. Returns matching excerpts with file and line number. " +
        "Use this instead of loading full files when you need a specific fact (e.g. 'auth flow', 'database schema', 'deploy').",
      inputSchema: {
        type: "object",
        properties: {
          query: { type: "string", description: "Keyword or phrase to search for" },
          limit: {
            type: "number",
            description: "Max results (default 5)",
          },
        },
        required: ["query"],
      },
    },
  ];

  const fullOnlyTools = [
    {
      name: "cai_check_drift",
      description:
        "Verify project docs match actual codebase. Returns drift score and issues. " +
        "Run this before making architectural changes or when you suspect docs are outdated.",
      inputSchema: { type: "object", properties: {} },
    },
    {
      name: "cai_context_for_task",
      description:
        "Get the most relevant project context for your current task within a token budget. " +
        "Provide a task description and max tokens — returns the best matching sections from all scaffold files. " +
        "Use this instead of loading individual files when you want smart context selection.",
      inputSchema: {
        type: "object",
        properties: {
          task: {
            type: "string",
            description: "What you're working on (e.g. 'fix auth middleware timeout')",
          },
          budget: {
            type: "number",
            description: "Max tokens to return (default 4000)",
          },
          prefer: {
            type: "array",
            items: { type: "string" },
            description: "Optional file hints to prioritize (e.g. ['architecture', 'conventions'])",
          },
          model: {
            type: "string",
            description:
              "Model name or tier to auto-set the budget. 'opus'/'expensive' → 2000 tokens, " +
              "'haiku'/'cheap'/'fast' → 8000, 'sonnet'/'medium' → 4000 (default). " +
              "Explicit budget param overrides this.",
          },
        },
        required: ["task"],
      },
    },
    {
      name: "cai_pattern_suggest",
      description:
        "Suggest relevant patterns for the current task. " +
        "Call this BEFORE starting a new task — if a relevant pattern exists, follow it instead of improvising. " +
        "Pass 'task' to search local project patterns by body text (FTS5). " +
        "Also searches the global pattern library by stack and dependency overlap.",
      inputSchema: {
        type: "object",
        properties: {
          task: {
            type: "string",
            description: "Short description of the task — used to search local patterns by body text.",
          },
        },
      },
    },
    ...getDynamicTools(projectModel),
  ];

  server.setRequestHandler(ListToolsRequestSchema, async () => ({
    tools: profile === "lean" ? coreTools : [...coreTools, ...fullOnlyTools],
  }));

  server.setRequestHandler(CallToolRequestSchema, async (req) => {
    const { name, arguments: args } = req.params;
    const result = await handleToolCall(name, args);

    // Telemetry: log this query for stats / hot-path scoring (fire-and-forget)
    const typedArgs = args as { path?: string; file?: string; section?: string } | undefined;
    const filePath = typedArgs?.path ?? typedArgs?.file;
    const sectionName = typedArgs?.section;
    const responseText = result.content?.[0]?.type === "text" ? result.content[0].text : "";
    appendQuery(projectRoot, {
      tool: name,
      file: filePath,
      section: sectionName,
      tokens: estimateTokens(responseText),
    });

    return result;
  });

  async function handleToolCall(name: string, args: unknown): Promise<{ content: Array<{ type: string; text: string }> }> {
    if (name === "cai_list_context") {
      const files = cache.getFileList();
      let totalTokens = 0;
      const rows = files.map((absPath) => {
        const rel = relative(projectRoot, absPath);
        const { tokens } = cache.getFile(absPath);
        totalTokens += tokens;
        const hint = tokens >= 3000 ? " ⚠ large" : tokens >= 1500 ? " · medium" : " · small";
        return `${rel.padEnd(40)} ~${tokens} tokens${hint}`;
      });
      rows.push("", `Total: ~${totalTokens} tokens across ${files.length} files`);
      return {
        content: [
          {
            type: "text",
            text: rows.join("\n"),
          },
        ],
      };
    }

    if (name === "cai_get_context") {
      const { path, mode = "summary", section } = args as { path: string; mode?: "full" | "summary" | "headings"; section?: string };
      const absPath = join(projectRoot, path);
      if (!existsSync(absPath)) {
        return { content: [{ type: "text", text: `File not found: ${path}` }] };
      }

      // Diff-based re-read: if Claude already read this file and it hasn't changed,
      // skip the full content to save tokens.
      const readState = cache.getReadState(absPath);
      if (readState === "unchanged" && !section) {
        return {
          content: [{
            type: "text",
            text: `# ${path} [unchanged since last read]\n\nFile has not changed since you last read it. Use the cached version.`,
          }],
        };
      }

      const { stripped: rawContent } = cache.getFile(absPath);
      cache.markRead(absPath);

      let content: string;
      let modeLabel: string;

      // Extract specific section if requested
      if (section) {
        content = extractSection(rawContent, section);
        modeLabel = content ? `section: ${section}` : "section not found";
        if (!content) {
          const headings = extractHeadings(rawContent);
          content = `Section "${section}" not found. Available headings:\n${headings}`;
        }
      } else if (mode === "headings") {
        content = extractHeadings(rawContent);
        modeLabel = "headings only";
      } else if (mode === "summary") {
        content = extractSummary(rawContent);
        modeLabel = "summary";
      } else {
        content = rawContent;
        modeLabel = "full";
      }

      // Freshness signal: show how stale the scaffold file might be
      const relPath = relative(projectRoot, absPath);
      const freshnessNote = await buildFreshnessNote(relPath, projectRoot);

      return {
        content: [
          {
            type: "text",
            text: `# ${path} [${modeLabel}]${freshnessNote}\n\n${content}`,
          },
        ],
      };
    }

    if (name === "cai_search") {
      const { query, limit } = args as { query: string; limit?: number };
      const maxResults = limit ?? 5;

      // Prefer FTS5/BM25 index; fall back to substring scan if SQLite unavailable
      const searchIdx = cache.getSearchIndex();
      // ADR supersede check — inject at top of results when query touches a decision
      const adrNote = resolveAdrStatus(query, cache.getAdrIndex());

      if (searchIdx) {
        const results = searchIdx.search(query, maxResults);
        if (results.length === 0 && !adrNote) {
          return {
            content: [{ type: "text", text: `No results for "${query}" in scaffold files.` }],
          };
        }
        const formatted = results
          .map((r) => `**${r.file}** · *${r.section}* (score: ${r.rank.toFixed(2)})\n\`\`\`\n${r.excerpt}\n\`\`\``)
          .join("\n\n");
        const ftsResult = [adrNote, formatted].filter(Boolean).join("\n\n");
        return { content: [{ type: "text", text: ftsResult }] };
      }

      // Fallback: substring search
      const files = cache.getFileList();
      const results = searchScaffold(files, projectRoot, query, cache, maxResults);
      if (results.length === 0 && !adrNote) {
        return {
          content: [{ type: "text", text: `No results for "${query}" in scaffold files.` }],
        };
      }
      const formatted = results
        .map((r) => `**${r.file}:${r.line}**\n\`\`\`\n${r.excerpt}\n\`\`\``)
        .join("\n\n");
      const parts = [adrNote, formatted].filter(Boolean).join("\n\n");
      return { content: [{ type: "text", text: parts }] };
    }

    if (name === "cai_context_for_task") {
      const { task, budget: rawBudget, prefer, model } = args as {
        task: string;
        budget?: number;
        prefer?: string[];
        model?: string;
      };

      // Model-aware budget: when caller specifies a model tier, use its default budget.
      // Explicit budget always wins. Model tiers steer toward token-cost-appropriate defaults.
      const modelBudget = resolveModelBudget(model);
      const budget = rawBudget ?? modelBudget;
      const index = cache.getSectionIndex();
      const edgeMap = cache.getEdgeMap();
      const queries = readQueries(projectRoot, { sinceMs: Date.now() - 7 * 24 * 60 * 60 * 1000 });
      const telemetry = aggregateByFile(queries);
      const result = routeContext({ task, budget, prefer }, index, telemetry, edgeMap);

      if (result.sections.length === 0) {
        return {
          content: [{
            type: "text",
            text: `No relevant context found for: "${task}"\nTry broader terms or use cai_list_context to see available files.`,
          }],
        };
      }

      const budgetLabel = model ? ` (budget: ${budget} for ${model})` : "";
      const parts: string[] = [
        `# Context for: ${task}`,
        `**${result.sections.length} sections** · ${result.total_tokens} tokens used · ${result.budget_remaining} remaining${budgetLabel}`,
        "",
      ];

      // Per-section freshness: this is the preferred agent entry point, but unlike
      // cai_get_context it emitted no staleness signal — a section from a 40-commits-old
      // doc could slip through unflagged. Compute a note per source file, cached per
      // relpath (sections share files; N sequential git spawns would blow the budget).
      // Flag stale sections, never drop them.
      const freshnessNotes = await buildSectionFreshnessNotes(
        result.sections.map((s) => s.id),
        (relPath) => buildFreshnessNote(relPath, projectRoot)
      );

      // Diff-aware dedup: when a section's source file was already read unchanged this
      // session, stub its content instead of re-pushing the bytes. Gate is file-level
      // (mirrors cai_get_context's whole-file model — sessionReads is file-granular).
      const dedup = partitionSectionsForDedup(
        result.sections.map((s) => s.id),
        (relPath) => {
          const absPath = join(projectRoot, relPath);
          return existsSync(absPath) && cache.getReadState(absPath) === "unchanged";
        }
      );
      const byId = new Map(dedup.map((d) => [d.id, d]));
      const filesToMark = new Set<string>();

      for (const s of result.sections) {
        const freshness = freshnessNotes.get(s.id) ?? "";
        const decision = byId.get(s.id);
        parts.push(`---`);
        parts.push(`**${s.id}** (${s.tokens} tokens, relevance ${s.score})${freshness}`);
        parts.push("");
        if (decision?.stub) {
          parts.push(`*[unchanged since you last read ${decision.relPath} — use the cached version]*`);
        } else {
          parts.push(s.content);
          if (decision) filesToMark.add(decision.relPath);
        }
        parts.push("");
      }

      // Record the files we actually emitted content for, at their current hash.
      for (const relPath of filesToMark) {
        const absPath = join(projectRoot, relPath);
        if (existsSync(absPath)) cache.markRead(absPath);
      }

      if (result.omitted.length > 0) {
        parts.push(`---`);
        parts.push(`*${result.omitted.length} sections omitted (budget exceeded):*`);
        for (const o of result.omitted.slice(0, 5)) {
          parts.push(`- ${o.id} (${o.tokens} tokens, score ${o.score})`);
        }
      }

      // Path anchoring: list verified paths so agents don't hallucinate file locations
      const verifiedPaths = buildVerifiedPaths(result.sections, cache.getKnownFilesSet());
      if (verifiedPaths.length > 0) {
        parts.push("");
        parts.push("---");
        parts.push("**Verified paths** (confirmed on disk — use these, do not guess alternatives):");
        for (const p of verifiedPaths) {
          parts.push(`- \`${p}\``);
        }
      }

      return { content: [{ type: "text", text: parts.join("\n") }] };
    }

    if (name === "cai_pattern_suggest") {
      try {
        const { task } = (args ?? {}) as { task?: string };
        const lines: string[] = [];

        // 1. Local project patterns — FTS5 body-text search when task is given
        if (task) {
          const searchIdx = cache.getSearchIndex();
          if (searchIdx) {
            const hits = searchIdx.search(task, 5);
            const patternHits = hits.filter((r) => r.file.includes("patterns/"));
            if (patternHits.length > 0) {
              lines.push("## Local patterns (by task match)");
              lines.push("");
              for (const h of patternHits) {
                lines.push(`**${h.file}** · *${h.section}* (score: ${h.rank.toFixed(2)})`);
                lines.push(`\`\`\`\n${h.excerpt}\n\`\`\``);
                lines.push("");
              }
            }
          }
        }

        // 2. Global library patterns — stack + dependency matching
        const { findMatching } = await import("../pattern/matching.js");
        const matches = findMatching(projectModel).slice(0, 5);
        if (matches.length > 0) {
          lines.push("## Global library patterns (by stack + deps)");
          lines.push("");
          for (const m of matches) {
            lines.push(`**${m.entry.name}** (score ${m.score})`);
            lines.push(`  ${m.entry.description}`);
            if (m.reasons.length > 0) lines.push(`  → ${m.reasons.join(" · ")}`);
            lines.push(`  Install with: cai pattern install ${m.entry.hash}`);
            lines.push("");
          }
        }

        if (lines.length === 0) {
          return {
            content: [{
              type: "text",
              text: task
                ? `No patterns found for "${task}".\nIf this kind of task recurs, create a pattern with: cai pattern share <name>`
                : "No matching patterns in the user's pattern library.\nIf this kind of task recurs, suggest the user share a pattern with: cai pattern share <name>",
            }],
          };
        }

        return { content: [{ type: "text", text: lines.join("\n") }] };
      } catch (err) {
        return {
          content: [{ type: "text", text: `Pattern suggest failed: ${(err as Error).message}` }],
        };
      }
    }

    if (name === "cai_check_drift") {
      cache.invalidate();
      const report = await runDriftCheck(config);
      const errors = report.issues.filter((i) => i.severity === "error").length;
      const warnings = report.issues.filter((i) => i.severity === "warning").length;
      const summary =
        report.score === 100
          ? "Scaffold is fully in sync — no drift detected."
          : `Drift score: ${report.score}/100. ${errors} error${errors !== 1 ? "s" : ""}, ${warnings} warning${warnings !== 1 ? "s" : ""}.`;
      const issues = report.issues
        .slice(0, 20)
        .map((i) => {
          const tier = i.claim?.confidence
            ? `/${i.claim.confidence}`
            : "";
          return `[${i.severity}${tier}] ${i.file}: ${i.message} (${i.code})`;
        })
        .join("\n");

      // Orphan detection (P2.3): patterns not queried via MCP in 30 days
      const orphanWarnings = detectOrphanedPatterns(projectRoot, scaffoldRoot);

      return {
        content: [
          {
            type: "text",
            text: [
              `${summary}\n\n${issues || "No issues."}`,
              orphanWarnings,
            ].filter(Boolean).join("\n\n"),
          },
        ],
      };
    }

    // Dynamic tools (project-type-dependent)
    const dynamicResult = handleDynamicTool(name, projectModel);
    if (dynamicResult !== null) {
      return { content: [{ type: "text", text: dynamicResult }] };
    }

    throw new Error(`Unknown tool: ${name}`);
  }

  const finalTransport = transport ?? new StdioServerTransport();
  await server.connect(finalTransport);
  return {
    server,
    close: async () => {
      cache.dispose();
      await server.close();
    },
  };
}
